import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SheetModal from '@/components/General/SheetModal';

/**
 * A text link that opens a SheetModal with arbitrary content.
 * Reusable for showing extended info/details behind a compact trigger.
 *
 * @param {string} props.linkText - The text shown on the link button.
 * @param {string} [props.iconName="information-circle-outline"] - Icon name for the link.
 * @param {string} [props.modalTitle] - Title for the opened modal. Defaults to linkText.
 * @param {string} [props.modalCancelText="Close"] - Cancel button text.
 * @param {string} [props.modalConfirmText] - If provided, shows a confirm button.
 * @param {Function} [props.onConfirm] - Confirm callback.
 * @param {string} [props.testID] - Test ID for the link button.
 * @param {string} [props.colorVariant="primary"] - Color variant for the link text/icon.
 * @param {React.ReactNode} props.children - Content rendered inside the modal.
 */
const LinkModal = ({
    linkText,
    iconName = 'information-circle-outline',
    modalTitle,
    modalCancelText = 'Close',
    modalConfirmText,
    onConfirm,
    testID,
    colorVariant = 'primary',
    children,
}) => {
    const [open, setOpen] = useState(false);
    const title = modalTitle || linkText;

    return (
        <>
            <Pressable
                testID={testID}
                onPress={() => setOpen(true)}
                style={styles.link}
            >
                {iconName && (
                    <Unicon name={iconName} size={14} colorVariant={colorVariant} />
                )}
                <Text style={styles.linkText(colorVariant)}>{linkText}</Text>
            </Pressable>

            <SheetModal
                isOpen={open}
                onCancel={() => setOpen(false)}
                onClose={() => setOpen(false)}
                title={title}
                cancelText={modalCancelText}
                confirmText={modalConfirmText}
                onConfirm={onConfirm}
            >
                <View style={styles.content}>
                    {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
                </View>
            </SheetModal>
        </>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    link: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 2,
        _web: {
            cursor: 'pointer',
        },
    },
    linkText: (colorVariant) => ({
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors[colorVariant],
    }),
    content: {
        fontFamily: fonts.regular,
    },
}));

export default React.memo(LinkModal);
